// index.tsx - الملف الكامل مع إضافة CashManagementPage
import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import './index.css';
import LoginPage from './LoginPage';
import ProtectedRoute from './ProtectedRoute';
import App from './App';

// ✅ Lazy Loading للصفحات - تتحمل فقط عند الحاجة
const AdminPage = lazy(() => import('./AdminPage'));
const SalesPage = lazy(() => import('./SalesPage'));
const SaleDetailsPage = lazy(() => import('./SaleDetailsPage'));
const CategoriesPage = lazy(() => import('./CategoriesPage'));
const SuppliersPage = lazy(() => import('./SuppliersPage'));
const InvoicesPage = lazy(() => import('./InvoicesPage'));
const InvoiceDetailsPage = lazy(() => import('./InvoiceDetailsPage'));
const SettingsPage = lazy(() => import('./SettingsPage'));
const BarcodePrintPage = lazy(() => import('./BarcodePrintPage'));
const BarcodePrintFromInvoicePage = lazy(() => import('./BarcodePrintFromInvoicePage'));
const LogsPage = lazy(() => import('./LogsPage'));
const DashboardPage = lazy(() => import('./DashboardPage'));
const ExpensesPage = lazy(() => import('./ExpensesPage'));
const SalariesPage = lazy(() => import('./SalariesPage'));
const MovementsPage = lazy(() => import('./MovementsPage'));
const CustomersPage = lazy(() => import('./CustomersPage'));
const OrdersPage = lazy(() => import('./OrdersPage'));
const AssetsPage = lazy(() => import('./AssetsPage'));
const FinancialReportsPage = lazy(() => import('./FinancialReportsPage'));
const SupplierStatsPage = lazy(() => import('./SupplierStatsPage'));
const TablesPage = lazy(() => import('./TablesPage'));  
const NewOrderPage = lazy(() => import('./NewOrderPage'));
const TaxReportsPage = lazy(() => import('./TaxReportsPage'));
const CustomersCRMPage = lazy(() => import('./CustomersCRMPage'));
const InventoryReportsPage = lazy(() => import('./InventoryReportsPage'));
const ReorderPage = lazy(() => import('./ReorderPage'));
const EmployeePerformancePage = lazy(() => import('./EmployeePerformancePage'));
const SaleReturnsPage = lazy(() => import('./SaleReturnsPage'));
const RecipePage = lazy(() => import('./RecipePage'));
const RawMaterialsPage = lazy(() => import('./RawMaterialsPage'));
const CreditCustomersPage = lazy(() => import('./CreditCustomersPage'));
const KitchenDisplayPage = lazy(() => import('./KitchenDisplayPage'));
const MaterialPurchasesPage = lazy(() => import('./MaterialPurchasesPage'));
const CashManagementPage = lazy(() => import('./CashManagementPage')); // ✅ إضافة صفحة الخزنة

// ✅ مكون Loader أثناء تحميل الصفحات
const PageLoader = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
    backgroundColor: '#f5f5f5'
  }}>
    <div className="loading-spinner" style={{ width: '50px', height: '50px' }}></div>
  </div>
);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

root.render(
  <React.StrictMode>
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ✅ صفحة تسجيل الدخول - عامة للجميع */}
          <Route path="/login" element={<LoginPage />} />
          
          {/* ✅ الصفحة الرئيسية - نقطة البيع */}
          <Route path="/" element={
            <ProtectedRoute requiredPermission="can_create_sale">
              <App />
            </ProtectedRoute>
          } />
          
          {/* ✅ صفحات العملاء (3 صفحات) */}
          <Route path="/customers" element={
            <ProtectedRoute requiredPermission="can_view_customers">
              <CustomersPage />
            </ProtectedRoute>
          } />
          
          <Route path="/crm" element={
            <ProtectedRoute requiredRole="admin">
              <CustomersCRMPage />
            </ProtectedRoute>
          } />
          
          <Route path="/credit-customers" element={
            <ProtectedRoute requiredPermission="can_view_customers">
              <CreditCustomersPage />
            </ProtectedRoute>
          } />
          
          {/* ✅ باقي المسارات المحمية */}
          <Route path="/admin" element={
            <ProtectedRoute requiredPermission="can_view_products">
              <AdminPage />
            </ProtectedRoute>
          } />
          
          <Route path="/assets" element={
            <ProtectedRoute requiredPermission="can_view_assets">
              <AssetsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/barcode-print" element={
            <ProtectedRoute requiredPermission="can_view_products">
              <BarcodePrintPage />
            </ProtectedRoute>
          } />
          
          <Route path="/barcode-print/:invoiceId" element={
            <ProtectedRoute requiredPermission="can_view_products">
              <BarcodePrintFromInvoicePage />
            </ProtectedRoute>
          } />
          
          <Route path="/categories" element={
            <ProtectedRoute requiredPermission="can_view_categories">
              <CategoriesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/dashboard" element={
            <ProtectedRoute requiredPermission="can_view_dashboard">
              <DashboardPage />
            </ProtectedRoute>
          } />
          
          <Route path="/employee-performance" element={
            <ProtectedRoute requiredRole="admin">
              <EmployeePerformancePage />
            </ProtectedRoute>
          } />
          
          <Route path="/expenses" element={
            <ProtectedRoute requiredPermission="can_view_expenses">
              <ExpensesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/financial-reports" element={
            <ProtectedRoute requiredRole="admin">
              <FinancialReportsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/inventory-reports" element={
            <ProtectedRoute requiredRole="admin">
              <InventoryReportsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/invoice/:id" element={
            <ProtectedRoute requiredPermission="can_view_invoices">
              <InvoiceDetailsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/invoices" element={
            <ProtectedRoute requiredPermission="can_view_invoices">
              <InvoicesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/kitchen" element={
            <ProtectedRoute requiredPermission="can_view_sales">
              <KitchenDisplayPage />
            </ProtectedRoute>
          } />
          
          <Route path="/logs" element={
            <ProtectedRoute requiredRole="admin">
              <LogsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/movements" element={
            <ProtectedRoute requiredPermission="can_view_movements">
              <MovementsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/orders/new" element={
            <ProtectedRoute requiredPermission="can_create_order">
              <NewOrderPage />
            </ProtectedRoute>
          } />
          
          <Route path="/orders" element={
            <ProtectedRoute requiredPermission="can_view_orders">
              <OrdersPage />
            </ProtectedRoute>
          } />
          
          {/* ✅ المواد الخام والوصفات */}
          <Route path="/raw-materials" element={
            <ProtectedRoute requiredPermission="can_view_raw_materials">
              <RawMaterialsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/recipes" element={
            <ProtectedRoute requiredPermission="can_view_recipes">
              <RecipePage />
            </ProtectedRoute>
          } />
          
          <Route path="/material-purchases" element={
            <ProtectedRoute requiredPermission="can_view_material_invoices">
              <MaterialPurchasesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/reorder" element={
            <ProtectedRoute requiredPermission="can_view_orders">
              <ReorderPage />
            </ProtectedRoute>
          } />
          
          <Route path="/returns" element={
            <ProtectedRoute requiredPermission="can_view_returns">
              <SaleReturnsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/sale/:id" element={
            <ProtectedRoute requiredPermission="can_view_sales">
              <SaleDetailsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/salaries" element={
            <ProtectedRoute requiredPermission="can_view_salaries">
              <SalariesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/sales" element={
            <ProtectedRoute requiredPermission="can_view_sales">
              <SalesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/settings" element={
            <ProtectedRoute requiredRole="admin">
              <SettingsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/supplier-stats" element={
            <ProtectedRoute requiredRole="admin">
              <SupplierStatsPage />
            </ProtectedRoute>
          } />
          
          <Route path="/suppliers" element={
            <ProtectedRoute requiredPermission="can_view_suppliers">
              <SuppliersPage />
            </ProtectedRoute>
          } />
          
          {/* ✅ مسار الترابيزات */}
          <Route path="/tables" element={
            <ProtectedRoute requiredPermission="can_view_tables">
              <TablesPage />
            </ProtectedRoute>
          } />
          
          <Route path="/tax-reports" element={
            <ProtectedRoute requiredRole="admin">
              <TaxReportsPage />
            </ProtectedRoute>
          } />
          
          {/* ✅ مسار إدارة الخزنة */}
          <Route path="/cash-management" element={
            <ProtectedRoute requiredPermission="can_view_financial">
              <CashManagementPage />
            </ProtectedRoute>
          } />
          
          {/* ✅ مسار 404 - الصفحة غير موجودة */}
          <Route path="*" element={
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              textAlign: 'center',
              backgroundColor: '#f5f5f5'
            }}>
              <h1 style={{ fontSize: '72px', color: '#f44336', margin: 0 }}>404</h1>
              <h2 style={{ color: '#333', marginBottom: '20px' }}>الصفحة غير موجودة</h2>
              <p style={{ color: '#666', marginBottom: '30px' }}>عذراً، الصفحة التي تبحث عنها غير موجودة</p>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4CAF50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '16px'
                }}
              >
                العودة للرئيسية
              </button>
            </div>
          } />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </React.StrictMode>
);